import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LineController
} from "chart.js";
import annotationPlugin from 'chartjs-plugin-annotation';
import NoData from "components/Common/NoData";

// Register necessary chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, LineController, annotationPlugin);

const BenchmarkingBarChart = ({data, tab, filterSelected, dottedLineValue, region}) => {
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Screen size detection
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    
    // Set initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup event listener
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isNoData = data?.message === "No data available";
  
  if (isNoData) {
    return <NoData/>;
  }

  // Custom tooltip to show region names
  const customTooltip = {
    callbacks: {
      label: function(context) {
        const datasetLabel = context.dataset.label;
        const value = context.parsed.y;
        if (tab === 'system' && datasetLabel === `${filterSelected ? filterSelected : 'All systems'} regional average score`) {
          const regionName = data[context.dataIndex].region;
          return [`${filterSelected ? filterSelected : 'All systems'} ${regionName} average score: ${value}`];
        }
        return `${datasetLabel}: ${value}`;
      }
    }
  };

  // Preparing the chart data
  const chartData = {
    labels: data?.map((item) => item.long_name || item.system_name),
    datasets: tab === 'system' ? [
      {
        label: `${filterSelected ? filterSelected : 'All systems'} average score`,
        data: data?.map((item) => filterSelected ? item.country_system_average : item.all_systems_average),
        backgroundColor: "#206A5D",
      },
      {
        label: `${filterSelected ? filterSelected : 'All systems'} regional average score`,
        data: data?.map((item) => filterSelected ? item.regional_system_average : item.regional_all_systems_average),
        backgroundColor: "#81B214",
        regionNames: data?.map(item => item.region),
      },
    ] : filterSelected ? [
      {
        label: `${filterSelected} average score`,
        data: data?.map((item) => item.average_score),
        backgroundColor: "#206A5D",
      },
      {
        label: `${region} average score`,
        data: data?.map((item) => item.regional_system_average),
        backgroundColor: "#81B214",
      },
      {
        label: 'All countries average score',
        data: data?.map((item) => item.all_countries_system_average),
        backgroundColor: "#FFCC29",
      },
    ] : [
      {
        label: "All countries average score",
        data: data?.map((item) => item.all_countries_average),
        backgroundColor: "#206A5D",
      },
      {
        label: "Southern Africa average score",
        data: data?.map((item) => item.southern_africa_average),
        backgroundColor: "#81B214",
      },
      {
        label: "East Africa average score",
        data: data?.map((item) => item.east_africa_average),
        backgroundColor: "#FFCC29",
      },
      {
        label: "West Africa average score",
        data: data?.map((item) => item.west_africa_average),
        backgroundColor: "#F58634",
      }, 
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: isSmallScreen ? 'y' : 'x', // Change to vertical bars on small screens
    scales: {
      x: {
        title: {
          display: true,
          text: isSmallScreen 
            ? "Policy Benchmarking Scores" 
            : (tab === 'system' ? "Countries" : 'Systems'),
          font: {
            size: 14,
          },
          color: 'green',
        },
      },
      y: {
        title: {
          display: true,
          text: isSmallScreen 
            ? (tab === 'system' ? "Countries" : 'Systems')
            : "Policy Benchmarking Scores",
          font: {
            size: 14,
          },
          color: 'green',
        },
        beginAtZero: true,
      },
    },
    plugins: {
      tooltip: customTooltip,
      legend: {
        position: "top",
        labels: {
          font: {
            size: 14,
          },
          generateLabels: (chart) => {
            const defaultLabels = Legend.defaults.labels.generateLabels(chart);
            
            if (dottedLineValue) {
              defaultLabels.push({
                text: `All countries ${filterSelected} average score (${dottedLineValue.toFixed(2)})`,
                strokeStyle: 'rgba(0, 128, 0, 1)',
                lineDash: [6, 6],
                lineWidth: 2,
                fillStyle: 'transparent',
              });
            }
            
            return defaultLabels;
          }
        },
        // Override the onClick handler to ignore clicks on the dotted line legend item
        onClick: (e, legendItem, legend) => {
          const isDottedLineLegend = legendItem.text && legendItem.text.includes(`All countries ${filterSelected} average score`);
          
          // If it's the dotted line legend, don't do anything
          if (isDottedLineLegend) {
            return;
          }
          
          // Otherwise, use the default legend click handler
          const index = legendItem.datasetIndex;
          const ci = legend.chart;
          
          if (ci.isDatasetVisible(index)) {
            ci.hide(index);
            legendItem.hidden = true;
          } else {
            ci.show(index);
            legendItem.hidden = false;
          }
        }
      },
      ...dottedLineValue && {annotation: {
        annotations: {
          line1: {
            type: 'line',
            [isSmallScreen ? 'xMin' : 'yMin']: dottedLineValue,
            [isSmallScreen ? 'xMax' : 'yMax']: dottedLineValue,
            borderColor: 'rgba(0, 128, 0, 1)',
            borderWidth: 2,
            borderDash: [6, 6],
          }
        }
      }},
    },
  };

  return (
    <div className="w-full border rounded-lg p-3">
      <div className="text-center text-lg p-3">
        {tab === 'system' 
          ? filterSelected 
            ? `${filterSelected} Policy Benchmarking: Country and Regional Analysis`
            : 'Policy Benchmarking Across Agri-Food Systems: Country and Regional Analysis'
          : filterSelected
            ? `Policy Benchmarking in ${filterSelected}: Local, Regional and Continental Insights`
            : 'Policy Benchmarking in Africa: Regional and Continental Insights'
        }
      </div>
      <div className={`flex items-center justify-center ${isSmallScreen ? 'h-auto' : 'h-[25rem]'}`}>
        <Bar data={chartData} options={options} className="max-w-full"/>
      </div>
    </div>
  );
};

export default BenchmarkingBarChart;